import { useState, useRef, useEffect, useMemo } from 'react';
import { Search, MapPin, Loader } from 'lucide-react';
import { useLeaflet } from '../hooks/useLeaflet';
import LoadingSpinner from './LoadingSpinner';

interface LocationData {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
}

interface LocationMapPickerProps {
  onLocationSelected: (location: LocationData) => void;
  initialLocation?: { lat: number; lng: number };
}

export default function LocationMapPicker({ onLocationSelected, initialLocation }: LocationMapPickerProps) {
  const [center, setCenter] = useState<[number, number]>(
    initialLocation ? [initialLocation.lat, initialLocation.lng] : [51.5074, -0.1278] // Default to London
  );
  const [selectedPosition, setSelectedPosition] = useState<[number, number] | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const mapRef = useRef<any>(null);
  const leaflet = useLeaflet();

  if (!leaflet) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '600px' }}>
        <LoadingSpinner text="Loading map..." />
      </div>
    );
  }

  const { L, components } = leaflet;
  const { MapContainer, TileLayer, Marker, useMapEvents } = components;

  useEffect(() => {
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    });
  }, [L]);

  const pickerIcon = useMemo(() => new L.DivIcon({
    className: 'custom-picker-marker',
    html: '<div style="background: #3fa0c9; color: white; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: bold; border: 3px solid white; box-shadow: 0 2px 12px rgba(63, 160, 201, 0.6);">📍</div>',
    iconSize: [32, 32],
    iconAnchor: [16, 32]
  }), [L]);

  // Component to handle map clicks
  function MapClickHandler({ onLocationClick }: { onLocationClick: (lat: number, lng: number) => void }) {
    useMapEvents({
      click(e) {
        onLocationClick(e.latlng.lat, e.latlng.lng);
      },
    });
    return null;
  }

  // Get user's current location on mount
  useEffect(() => {
    if (!initialLocation && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newCenter: [number, number] = [position.coords.latitude, position.coords.longitude];
          setCenter(newCenter);
          if (mapRef.current) {
            mapRef.current.setView(newCenter, 13);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
        }
      );
    }
  }, [initialLocation]);

  const handleMapClick = async (lat: number, lng: number) => {
    const position: [number, number] = [lat, lng];
    setSelectedPosition(position);
    
    // Reverse geocode to get location name
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
      );
      const data = await response.json();
      
      // Extract name and address
      const name = data.name || data.display_name?.split(',')[0] || 'Selected Location';
      const address = data.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      
      setLocationName(name);
      setLocationAddress(address);
    } catch (err) {
      console.error('Reverse geocoding error:', err);
      setLocationName('Selected Location');
      setLocationAddress(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&limit=5`
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectSearchResult = (result: any) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    const position: [number, number] = [lat, lng];
    
    setSelectedPosition(position);
    setCenter(position);
    setLocationName(result.name || result.display_name.split(',')[0]);
    setLocationAddress(result.display_name);
    setSearchResults([]);
    setSearchQuery('');
    
    if (mapRef.current) {
      mapRef.current.setView(position, 15);
    }
  };

  const handleConfirm = () => {
    if (selectedPosition) {
      onLocationSelected({
        name: locationName,
        address: locationAddress,
        latitude: selectedPosition[0],
        longitude: selectedPosition[1]
      });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', height: '600px' }}>
      {/* Search Bar */}
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={20} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search for a place..."
              style={{
                width: '100%',
                padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                backgroundColor: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '0.875rem'
              }}
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={searching || !searchQuery.trim()}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: 'var(--color-accent)',
              color: '#0b0b0b',
              border: 'none',
              borderRadius: '8px',
              cursor: searching ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              opacity: searching || !searchQuery.trim() ? 0.5 : 1
            }}
          >
            {searching ? <Loader size={16} /> : 'Search'}
          </button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '0.5rem',
            backgroundColor: '#1a1a1a',
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000
          }}>
            {searchResults.map((result, idx) => (
              <button
                key={idx}
                onClick={() => handleSelectSearchResult(result)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: idx < searchResults.length - 1 ? '1px solid var(--color-border)' : 'none',
                  color: 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-card)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{ fontWeight: '500' }}>{result.name || result.display_name.split(',')[0]}</div>
                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.25rem' }}>
                  {result.display_name}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Map */}
      <div style={{ flex: 1, borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--color-border)' }}>
        <MapContainer
          center={center}
          zoom={13}
          zoomControl={false}
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler onLocationClick={handleMapClick} />
          
          {selectedPosition && (
            <Marker position={selectedPosition} icon={pickerIcon} />
          )}
        </MapContainer>
      </div>

      {/* Selected Location Info */}
      {selectedPosition && (
        <div style={{
          padding: '1rem',
          backgroundColor: 'var(--color-card)',
          border: '1px solid var(--color-secondary)',
          borderRadius: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
            <MapPin size={20} style={{ color: 'var(--color-secondary)', flexShrink: 0, marginTop: '0.25rem' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                <input
                  type="text"
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  placeholder="Location name"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    backgroundColor: '#0b0b0b',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <div style={{ fontSize: '0.875rem', color: '#cfcfcf' }}>
                {locationAddress}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.5rem' }}>
                {selectedPosition[0].toFixed(5)}, {selectedPosition[1].toFixed(5)}
              </div>
            </div>
          </div>
          
          <button
            onClick={handleConfirm}
            style={{
              width: '100%',
              marginTop: '1rem',
              padding: '0.75rem',
              backgroundColor: 'var(--color-secondary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}
          >
            Use This Location
          </button>
        </div>
      )}

      <style>{`
        .custom-picker-marker {
          background: transparent;
          border: none;
        }
      `}</style>
    </div>
  );
}



